import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { BASELINE } from "../../util";
import { motion, AnimatePresence } from "framer-motion";

// ── Step message definitions ──────────────────────────────────────────────────
const STEP_MESSAGES = {
  2: [{ text: "Have you taken the SAT before?" }],
  4: [{ text: "How many hours per week can you dedicate to SAT prep?" }],
  6: [{ text: "Quick tip — click Mock SAT to test your current level and see where you stand.", img: "/dashboard/1.gif" }],
  7: [{ text: "In your Dashboard, go to Personalized Test to create custom practice sets tailored to you.", img: "/dashboard/2.gif" }],
  8: [{ text: "We also have a ready-to-use E-Book to make studying way easier.", img: "/dashboard/3.gif" }],
  9: [{ text: "You're all set! Welcome to P-Vault — let's crush that SAT! 🎯" }],
};

// Auto-skip steps (no messages, no input)
const AUTO_SKIP = new Set([3, 5]);

// ── Bot avatar ────────────────────────────────────────────────────────────────
const BotAvatar = ({ size = 8 }) => (
  <div
    className={`w-${size} h-${size} rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-indigo-500/20`}
  >
    <span className="text-white font-bold" style={{ fontSize: size * 1.5 }}>P</span>
  </div>
);

// ── Typing dots ───────────────────────────────────────────────────────────────
const TypingBubble = () => (
  <motion.div
    key="typing"
    initial={{ opacity: 0, y: 6, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, scale: 0.95 }}
    className="flex items-end gap-2"
  >
    <BotAvatar size={7} />
    <div className="px-4 py-3 rounded-[20px] rounded-bl-[6px] border border-white/[0.07]"
      style={{ background: "rgba(255,255,255,0.06)" }}>
      <div className="flex items-center gap-[5px]">
        {[0, 1, 2].map(i => (
          <motion.div
            key={i}
            className="w-[7px] h-[7px] rounded-full bg-white/40"
            animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15 }}
          />
        ))}
      </div>
    </div>
  </motion.div>
);

// ── Message bubble ────────────────────────────────────────────────────────────
const MessageBubble = ({ msg }) => (
  <motion.div
    initial={{ opacity: 0, y: 10, scale: 0.96 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.22, ease: "easeOut" }}
    className={`flex items-end gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
  >
    {msg.role === "ai" && <BotAvatar size={7} />}
    <div
      className={`max-w-[78%] px-4 py-2.5 text-sm leading-relaxed ${
        msg.role === "user"
          ? "bg-indigo-600 text-white rounded-[20px] rounded-br-[6px]"
          : "text-white/90 rounded-[20px] rounded-bl-[6px] border border-white/[0.07]"
      }`}
      style={msg.role === "ai" ? { background: "rgba(255,255,255,0.06)" } : {}}
    >
      <span>{msg.text}</span>
      {msg.img && (
        <img
          src={msg.img}
          alt="Tutorial"
          className="mt-2.5 rounded-xl w-full object-cover border border-white/10"
          onError={e => { e.target.style.display = "none"; }}
        />
      )}
    </div>
  </motion.div>
);

// ── Pill button ───────────────────────────────────────────────────────────────
const PillBtn = ({ children, onClick, variant = "ghost", disabled = false, className = "" }) => {
  const base = "py-3 px-5 rounded-2xl font-semibold text-sm transition-all duration-150 active:scale-[0.97] disabled:opacity-40 disabled:pointer-events-none";
  const variants = {
    primary: "bg-indigo-600 hover:bg-indigo-500 text-white",
    ghost:   "bg-white/10 hover:bg-white/[0.17] text-white",
    finish:  "bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white shadow-lg shadow-indigo-500/20",
  };
  return (
    <button className={`${base} ${variants[variant]} ${className}`} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
function DarkSatModal({ user, isOpen, onClose }) {
  const [takenSat, setTakenSat]       = useState(null);
  const [mathScore, setMathScore]     = useState("");
  const [engScore, setEngScore]       = useState("");
  const [studyTime, setStudyTime]     = useState("");
  const [familiarWithAi]              = useState(null);
  const [messages, setMessages]       = useState([]);
  const [step, setStep]               = useState(null);
  const [isTyping, setIsTyping]       = useState(false);
  const [inputLocked, setInputLocked] = useState(false);
  const messagesEndRef                = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // ── Reset on open ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    setMessages([]);
    setIsTyping(false);
    setInputLocked(false);
    setTakenSat(null);
    setMathScore("");
    setEngScore("");
    setStudyTime("");
    setStep(2);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Trigger AI messages when step changes ───────────────────────────────────
  useEffect(() => {
    if (!isOpen || step === null) return;
    if (AUTO_SKIP.has(step)) { setTimeout(() => setStep(s => s + 1), 150); return; }
    const lines = STEP_MESSAGES[step];
    if (!lines) return;

    setInputLocked(true);
    setIsTyping(true);

    let cumulativeDelay = 700;
    lines.forEach((line, i) => {
      // Show message after typing delay
      setTimeout(() => {
        setIsTyping(false);
        setMessages(prev => [...prev, { id: `${step}-${i}-${Date.now()}`, role: "ai", ...line }]);
        scrollToBottom();
        if (i < lines.length - 1) {
          setTimeout(() => setIsTyping(true), 250);
        } else {
          setInputLocked(false);
        }
      }, cumulativeDelay);
      cumulativeDelay += 1100;
    });
  }, [step, isOpen, scrollToBottom]);

  useEffect(() => { scrollToBottom(); }, [messages, isTyping, scrollToBottom]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const pushUser = useCallback((text) => {
    setInputLocked(true);
    setMessages(prev => [...prev, { id: `u-${Date.now()}`, role: "user", text }]);
    scrollToBottom();
  }, [scrollToBottom]);

  const pushAI = useCallback((text, delay = 600) => {
    setTimeout(() => {
      setMessages(prev => [...prev, { id: `a-${Date.now()}`, role: "ai", text }]);
      scrollToBottom();
    }, delay);
  }, [scrollToBottom]);

  const resetAndClose = useCallback(() => {
    setMessages([]); setStep(null); setIsTyping(false); setInputLocked(false);
    setTakenSat(null); setMathScore(""); setEngScore(""); setStudyTime("");
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    const satScore = takenSat === "yes" ? `${mathScore},${engScore}` : null;
    try {
      await axios.post(`${BASELINE}satsurvey`, {
        user_id: user.u_id, takenSat, satScore,
        practiceArea: null, studyTime, usedAi: familiarWithAi,
      });
    } catch (e) { console.error(e); }
    resetAndClose();
  }, [takenSat, mathScore, engScore, studyTime, familiarWithAi, user, resetAndClose]);

  // ── Input area per step ───────────────────────────────────────────────────────
  const renderInput = () => {
    switch (step) {
      case 2:
        return (
          <div className="flex flex-col gap-3">
            <div className="flex gap-2.5">
              <PillBtn
                variant={takenSat === "yes" ? "primary" : "ghost"}
                className="flex-1"
                onClick={() => setTakenSat("yes")}
              >
                Yes
              </PillBtn>
              <PillBtn
                variant={takenSat === "no" ? "primary" : "ghost"}
                className="flex-1"
                onClick={() => {
                  setTakenSat("no");
                  pushUser("No, not yet.");
                  setTimeout(() => setStep(4), 400);
                }}
              >
                Not yet
              </PillBtn>
            </div>

            <AnimatePresence>
              {takenSat === "yes" && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  className="flex flex-col gap-2"
                >
                  <div className="flex gap-2">
                    <input
                      type="number" placeholder="English (200–800)"
                      min="200" max="800" value={engScore}
                      onChange={e => setEngScore(e.target.value)}
                      onBlur={e => {
                        const n = Number(e.target.value);
                        if (n && n < 200) setEngScore("200");
                        else if (n > 800) setEngScore("800");
                      }}
                      className="flex-1 bg-white/[0.08] text-white placeholder-white/25 rounded-2xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-500/60 border border-white/[0.06] transition-all no-spinner"
                    />
                    <input
                      type="number" placeholder="Math (200–800)"
                      min="200" max="800" value={mathScore}
                      onChange={e => setMathScore(e.target.value)}
                      onBlur={e => {
                        const n = Number(e.target.value);
                        if (n && n < 200) setMathScore("200");
                        else if (n > 800) setMathScore("800");
                      }}
                      className="flex-1 bg-white/[0.08] text-white placeholder-white/25 rounded-2xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-500/60 border border-white/[0.06] transition-all no-spinner"
                    />
                  </div>
                  <PillBtn
                    variant="primary" className="w-full"
                    disabled={!mathScore || !engScore}
                    onClick={() => {
                      pushUser(`English: ${engScore} · Math: ${mathScore}`);
                      setTimeout(() => setStep(4), 400);
                    }}
                  >
                    Continue
                  </PillBtn>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );

      case 4:
        return (
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "Less than 5 hrs", value: "0" },
              { label: "5 – 10 hrs",      value: "5" },
              { label: "10 – 15 hrs",     value: "10" },
              { label: "15+ hrs",          value: "15" },
            ].map(({ label, value }) => (
              <PillBtn
                key={value} variant="ghost"
                className="text-center"
                onClick={() => {
                  setStudyTime(value);
                  pushUser(`${label} / week`);
                  setTimeout(() => setStep(6), 400);
                }}
              >
                {label}
              </PillBtn>
            ))}
          </div>
        );

      case 6:
      case 7:
      case 8:
        return (
          <PillBtn variant="primary" className="w-full"
            onClick={() => { pushUser("Got it!"); setTimeout(() => setStep(step + 1), 400); }}>
            Got it →
          </PillBtn>
        );

      case 9:
        return (
          <PillBtn variant="finish" className="w-full"
            onClick={() => { pushUser("Let's go!"); setTimeout(() => handleSubmit(), 500); }}>
            Let's get started →
          </PillBtn>
        );

      default:
        return null;
    }
  };

  // ── Progress ──────────────────────────────────────────────────────────────────
  const STEP_ORDER = [2, 4, 6, 7, 8, 9];
  const progressIdx = STEP_ORDER.indexOf(step ?? 0);
  const progress = progressIdx < 0 ? 0 : progressIdx;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/75 backdrop-blur-[6px]"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, y: 48 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 48 }}
        transition={{ type: "spring", damping: 30, stiffness: 320 }}
        className="relative w-full sm:max-w-[420px] flex flex-col rounded-t-[28px] sm:rounded-[28px] overflow-hidden shadow-2xl border border-white/[0.07]"
        style={{
          height: "min(86vh, 620px)",
          background: "linear-gradient(160deg, #13141f 0%, #0e0f18 100%)",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-white/[0.06] flex-shrink-0">
          <BotAvatar size={9} />
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-[15px] leading-tight">P-Vault Assistant</p>
            <p className="text-[11px] text-emerald-400 flex items-center gap-1.5 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0 animate-pulse" />
              Online
            </p>
          </div>
          {/* Progress dots */}
          <div className="flex items-center gap-[5px]">
            {STEP_ORDER.map((_, i) => (
              <div key={i}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i <= progress ? 8 : 6,
                  height: i <= progress ? 8 : 6,
                  background: i < progress ? "#6366f1" : i === progress ? "#818cf8" : "rgba(255,255,255,0.12)",
                }}
              />
            ))}
          </div>
          {/* Close */}
          <button
            onClick={onClose}
            className="ml-1 w-7 h-7 flex items-center justify-center rounded-full text-white/30 hover:text-white/70 hover:bg-white/10 transition-all flex-shrink-0"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* ── Messages ── */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-hide">
          <AnimatePresence initial={false}>
            {messages.map(msg => (
              <MessageBubble key={msg.id} msg={msg} />
            ))}
            {isTyping && <TypingBubble key="typing-indicator" />}
          </AnimatePresence>
          <div ref={messagesEndRef} />
        </div>

        {/* ── Input area ── */}
        <div
          className="px-4 pt-3 pb-5 border-t border-white/[0.05] flex-shrink-0"
          style={{ minHeight: 80 }}
        >
          <AnimatePresence mode="wait">
            {!inputLocked && step !== null && (
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
              >
                {renderInput()}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

export default DarkSatModal;
